import React, { useState } from "react";

const WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];

const startOfMonth = (date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const addMonths = (date, n) =>
  new Date(date.getFullYear(), date.getMonth() + n, 1);

const isSameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const toDate = (d) => (d instanceof Date ? d : new Date(d));

const buildMonthGrid = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const gridStart = new Date(year, month, 1 - firstDay.getDay());
  const weeks = [];
  for (let w = 0; w < 6; w++) {
    const row = [];
    for (let d = 0; d < 7; d++) {
      const day = new Date(
        gridStart.getFullYear(),
        gridStart.getMonth(),
        gridStart.getDate() + w * 7 + d,
      );
      row.push(day);
    }
    weeks.push(row);
  }
  return weeks;
};

const formatMonthTitle = (date) => {
  const month = date.toLocaleString("pt-BR", { month: "long" });
  const year = date.getFullYear();
  return `${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
};

const today = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/**
 * Calendário multi-mês com seleção de múltiplas datas individuais.
 * Renderiza N meses lado a lado, com navegação por chevrons nas extremidades.
 *
 * Estados visuais:
 * - default: número em $blue_primary
 * - hover: bolha $light_blue_02
 * - selected: bolha $blue_primary com texto branco
 * - past: texto $color-cinza2-5 (datas anteriores a hoje, não clicáveis)
 * - disabled: bolha $color-cinza2 (passadas via prop, não clicáveis)
 * - other-month: célula vazia (preenchimento da grade)
 *
 * @param {Object} props
 * @param {number} [props.numberOfMonths=2] - Quantidade de meses lado a lado (1, 2, 3...)
 * @param {Array<Date|string|number>} [props.selectedDates=[]] - Datas atualmente selecionadas (controlado)
 * @param {(dates: Date[]) => void} [props.onChange] - Callback chamado a cada clique de dia
 * @param {Date|string} [props.startMonth] - Mês inicial a exibir (default: mês atual)
 * @param {Array<Date|string|number>} [props.disabledDates=[]] - Lista de datas a desabilitar (aceita Date, ISO string, JSON)
 * @param {Date|string} [props.minDate] - Data mínima selecionável
 * @param {Date|string} [props.maxDate] - Data máxima selecionável
 *
 * @example <caption>1 mês básico</caption>
 * const [dates, setDates] = useState([]);
 * <CalendarMultiMonth
 *   numberOfMonths={1}
 *   selectedDates={dates}
 *   onChange={setDates}
 * />
 *
 * @example <caption>2 meses lado a lado (default)</caption>
 * <CalendarMultiMonth
 *   selectedDates={dates}
 *   onChange={setDates}
 * />
 *
 * @example <caption>3 meses com datas desabilitadas via JSON (ISO strings)</caption>
 * <CalendarMultiMonth
 *   numberOfMonths={3}
 *   selectedDates={dates}
 *   onChange={setDates}
 *   disabledDates={["2026-06-18", "2026-07-01", "2026-07-14"]}
 * />
 *
 * @example <caption>Iniciar em mês específico + min/max</caption>
 * <CalendarMultiMonth
 *   numberOfMonths={2}
 *   startMonth="2026-09-01"
 *   minDate="2026-09-10"
 *   maxDate="2026-10-25"
 *   selectedDates={dates}
 *   onChange={setDates}
 * />
 */
const CalendarMultiMonth = ({
  numberOfMonths = 2,
  selectedDates = [],
  onChange = () => {},
  startMonth,
  disabledDates = [],
  availableDates,
  minDate,
  maxDate,
}) => {
  const initialStart = startOfMonth(
    startMonth ? toDate(startMonth) : new Date(),
  );
  const [currentStart, setCurrentStart] = useState(initialStart);

  const normalizedSelected = selectedDates.map(toDate);
  const normalizedDisabled = disabledDates.map(toDate);
  const normalizedAvailable = Array.isArray(availableDates)
    ? availableDates.map(toDate)
    : null;
  const min = minDate ? toDate(minDate) : null;
  const max = maxDate ? toDate(maxDate) : null;
  const todayDate = today();

  const isSelected = (day) =>
    normalizedSelected.some((d) => isSameDay(d, day));

  const isDisabled = (day) => {
    if (normalizedDisabled.some((d) => isSameDay(d, day))) return true;
    if (min && day < new Date(min.getFullYear(), min.getMonth(), min.getDate()))
      return true;
    if (max && day > new Date(max.getFullYear(), max.getMonth(), max.getDate()))
      return true;
    if (
      normalizedAvailable &&
      !normalizedAvailable.some((d) => isSameDay(d, day))
    )
      return true;
    return false;
  };

  const isPast = (day) => day < todayDate;

  const handleDayClick = (day, monthDate) => {
    if (isDisabled(day) || isPast(day)) return;
    if (day.getMonth() !== monthDate.getMonth()) return;
    const exists = normalizedSelected.find((d) => isSameDay(d, day));
    const next = exists
      ? normalizedSelected.filter((d) => !isSameDay(d, day))
      : [...normalizedSelected, day];
    onChange(next);
  };

  const goPrev = () => setCurrentStart((s) => addMonths(s, -1));
  const goNext = () => setCurrentStart((s) => addMonths(s, 1));

  const months = Array.from({ length: numberOfMonths }, (_, i) =>
    addMonths(currentStart, i),
  );

  return (
    <div className="calendar-multi-month">
      {months.map((monthDate, idx) => {
        const grid = buildMonthGrid(monthDate);
        const isFirst = idx === 0;
        const isLast = idx === months.length - 1;
        return (
          <div className="calendar-month" key={monthDate.toISOString()}>
            <div className="calendar-month-header">
              <button
                type="button"
                className="calendar-nav-btn"
                onClick={goPrev}
                disabled={!isFirst}
                aria-label="Mês anterior"
              >
                <i className="fas fa-chevron-left" />
              </button>
              <span>{formatMonthTitle(monthDate)}</span>
              <button
                type="button"
                className="calendar-nav-btn"
                onClick={goNext}
                disabled={!isLast}
                aria-label="Próximo mês"
              >
                <i className="fas fa-chevron-right" />
              </button>
            </div>

            <div className="calendar-weekdays">
              {WEEKDAYS.map((wd, i) => (
                <span key={i}>{wd}</span>
              ))}
            </div>

            <div className="calendar-days">
              {grid.flat().map((day, i) => {
                const otherMonth = day.getMonth() !== monthDate.getMonth();
                const disabled = !otherMonth && isDisabled(day);
                const past = !disabled && !otherMonth && isPast(day);
                const selected = !otherMonth && isSelected(day);
                const sunday = !otherMonth && day.getDay() === 0;
                const classes = [
                  "calendar-day",
                  otherMonth && "calendar-day--other-month",
                  disabled && "calendar-day--disabled",
                  past && "calendar-day--past",
                  selected && "calendar-day--selected",
                  sunday && "calendar-day--sunday",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <div
                    key={i}
                    className={classes}
                    onClick={() => handleDayClick(day, monthDate)}
                  >
                    {otherMonth ? "" : day.getDate()}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CalendarMultiMonth;
